import moment from "moment";
import { useTranslation } from "react-i18next";
import StarRatings from "react-star-ratings";
import "moment/locale/ar";
import DetailCollapseSection from "./DetailCollapseSection";

interface Props {
  reviewNumber: number;
  rateNumber: any;
  review_ids: any;
  sectionId: string;
  isOpen: boolean;
  onToggle: (id: string) => void;
}

const RateAndReview = ({
  reviewNumber,
  rateNumber,
  review_ids,
  sectionId,
  isOpen,
  onToggle,
}: Props) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const count = review_ids?.length ?? 0;

  const icon = (
    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
      <path
        d="M10 2.5l2.35 4.76 5.25.76-3.8 3.7.9 5.24L10 14.77l-4.7 2.47.9-5.24-3.8-3.7 5.25-.76L10 2.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <DetailCollapseSection
      sectionId={sectionId}
      title={t("menu.rate")}
      icon={icon}
      count={reviewNumber > 0 ? reviewNumber : undefined}
      isOpen={isOpen}
      onToggle={onToggle}
      isRtl={isRtl}
    >
      <div className="ac-shop-detail__review-summary">
        <div className="ac-shop-detail__review-score-ring">
          <span className="ac-shop-detail__review-score-num">
            {rateNumber ?? "—"}
          </span>
          <span className="ac-shop-detail__review-score-max">/5</span>
        </div>
        <div className="ac-shop-detail__review-summary-meta">
          <StarRatings
            rating={Number(rateNumber) || 0}
            starRatedColor="#c9a227"
            starEmptyColor="var(--ac-theme-border)"
            numberOfStars={5}
            starDimension="18px"
            starSpacing="2px"
            name="product-rating-summary"
          />
          <p className="ac-shop-detail__review-count mb-0">
            {reviewNumber} {t("menu.reviews")}
          </p>
        </div>
      </div>

      <div className="ac-shop-detail__review-list">
        {count > 0 ? (
          review_ids.map((item: any, index: number) => {
            const authorLabel =
              item.partner_id?.split(",")[1]?.trim() ??
              item.partner_id ??
              t("menu.reviews");
            const initial = String(authorLabel).trim().charAt(0).toUpperCase();

            return (
              <article
                key={item?.id ?? index}
                className="ac-shop-detail__review-card"
              >
                <div className="ac-shop-detail__review-card-head">
                  <div className="ac-shop-detail__review-author-block">
                    <span className="ac-shop-detail__review-avatar" aria-hidden>
                      {initial}
                    </span>
                    <div>
                      <p className="ac-shop-detail__review-author mb-0">
                        {authorLabel}
                      </p>
                      <p className="ac-shop-detail__review-date mb-0">
                        {moment(item.create_date)
                          .locale(isRtl ? "ar" : "en")
                          .format("MMMM D, YYYY")}
                      </p>
                    </div>
                  </div>
                  <StarRatings
                    rating={item.rate}
                    starRatedColor="var(--ac-theme-accent)"
                    starEmptyColor="var(--ac-theme-border)"
                    numberOfStars={5}
                    starDimension="16px"
                    starSpacing="2px"
                    name={`review-${index}`}
                  />
                </div>
                {item.message ? (
                  <p className="ac-shop-detail__review-message mb-0">
                    {item.message}
                  </p>
                ) : null}
              </article>
            );
          })
        ) : (
          <div className="ac-shop-detail__empty">
            <span className="ac-shop-detail__empty-icon" aria-hidden>
              <svg viewBox="0 0 24 24" fill="none" width="28" height="28">
                <path
                  d="M12 2l2.4 7.4h7.8l-6.3 4.6 2.4 7.4L12 17l-6.3 4.4 2.4-7.4-6.3-4.6h7.8L12 2Z"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <p className="ac-shop-detail__empty-text">{t("services.message")}</p>
          </div>
        )}
      </div>
    </DetailCollapseSection>
  );
};

export default RateAndReview;
